import { useEffect, useState, type ReactNode } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import type {
  SicherheitstechnikStand, StNotstrom, StRing, StRwaLinie, StRwaZentrale, StVorgabe, StZeile,
} from './elektro/sicherheitstechnik';
import {
  ANNAHMEN_HINWEIS, PDF_HINWEIS, spannungEnde, statusText, zellenzahl, zerlegeBefund,
} from './elektro/sicherheitstechnik';
import { PageHeader } from '../rahmen/PageHeader';
import { SicherheitsReiter } from './elektro/SicherheitsReiter';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Brandfallsteuerung und Notstrom (W2-D1, 16.10.2026) — seit BM-1 im Abschnitt
 * „Brandmeldeanlage" (vorher „Melde- und Sicherheitstechnik").
 *
 * Die Seite LIEST `GET …/elektro/sicherheitstechnik`: Brandfallsteuermatrix je
 * Brandabschnitt, Notstrom der BMZ und RWA-Zentralen, Ringnachweis und
 * RWA-Antriebsleitungen. Gerechnet wird auf dem Server, die Seite schreibt nichts.
 *
 * Zwei PDF-Knöpfe (ELT-BFS, ELT-BNW) und die Excel-Mappe. Der PDF-Abruf legt nur bei
 * geändertem Stand eine neue Berichtsfassung an, sonst die abgelegte (F11) — das steht am Knopf und über der Seite.
 *
 * Sprachalarmanlage (W2-D2) und Zutritt (W2-D3) waren Reiter dieser Seite. Seit BM-5 sind es LocalTabs auf ihre
 * eigenen Routen `elektro-sprachalarm` und `elektro-zutritt` (S1.1, seit BM-1 E2 vorhanden); diese Seite lädt nur
 * noch ihre eigene JSON-Route.
 */
export function ElektroSicherheitstechnik() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <SicherheitstechnikAnsicht api={api} projektId={projekt.id}
                                    reiter={<SicherheitsReiter projektId={projekt.id} />} />;
}

function zustandVon(status: string): Zustand {
  return status === 'fehler' ? 'fehler' : status === 'warnung' ? 'warnung' : 'gut';
}

function Statusmarke({ status, gegenAnnahmen }: { status: string; gegenAnnahmen: boolean }) {
  return <StatusBadge zustand={zustandVon(status)} text={statusText(status, gegenAnnahmen)} klein />;
}

function Befundliste({ befunde }: { befunde: string[] }) {
  if (befunde.length === 0) return null;
  return (
    <ul className="liste">
      {befunde.map((b, i) => {
        const { stufe, text } = zerlegeBefund(b);
        const z: Zustand = stufe === 'fehler' ? 'fehler' : stufe === 'warnung' ? 'warnung' : 'info';
        return <li key={`${i}-${text.slice(0, 24)}`}><StatusBadge zustand={z} text={text} /></li>;
      })}
    </ul>
  );
}

export function SicherheitstechnikAnsicht({ api, projektId, reiter }: {
  api: ApiClient; projektId: string; reiter?: ReactNode;
}) {
  const [stand, setStand] = useState<SicherheitstechnikStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api.elektroSicherheitstechnik(projektId)
      .then((s) => { if (aktiv) { setStand(s); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  async function laden(art: 'matrix' | 'nachweis' | 'excel') {
    setLaeuft(art);
    try {
      if (art === 'matrix') await api.elektroSicherheitstechnikMatrixLaden(projektId);
      else if (art === 'nachweis') await api.elektroSicherheitstechnikNachweisLaden(projektId);
      else await api.elektroSicherheitstechnikListen(projektId);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  const aktionen = (
    <>
      <button type="button" className="m-knopf" disabled={laeuft !== null}
              onClick={() => { void laden('excel'); }}>
        Excel-Mappe
      </button>
      <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
              onClick={() => { void laden('matrix'); }}>
        Steuermatrix als PDF
      </button>
      <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
              onClick={() => { void laden('nachweis'); }}>
        Nachweis als PDF
      </button>
    </>
  );

  return (
    <div className="seite">
      <PageHeader
        titel="Brandfallsteuerung und Notstrom"
        beschreibung="Brandfallsteuermatrix je Brandabschnitt, Notstrom der BMZ und RWA-Zentralen nach DIN EN 54-4, Ringnachweis BMA und RWA-Antriebsleitungen; Sprachalarmanlage und Türabgleich Zutritt, Fluchttür, Türüberwachung in eigenen Reitern. Gerechnet beim Abruf, nur lesend."
        aktionen={aktionen}
      />
      {reiter}
      <p className="hinweis">{PDF_HINWEIS}</p>
      {fehler && <ErrorState titel="Die Brandfallsteuerung konnte nicht geladen werden." technisch={fehler} />}
      {!fehler && !stand && <LoadingState />}
      {stand && stand.kennzahlen.zentralen === 0 && (
        <EmptyState titel="Keine Zentrale angelegt"
                    erklaerung="Zentralen und Linien entstehen über „Zentralen, Meldergruppen und Ringe“ (Zentralen erkennen, Linien bilden)." />
      )}
      {stand && stand.kennzahlen.zentralen > 0 && <Inhalt stand={stand} />}
    </div>
  );
}

function Inhalt({ stand }: { stand: SicherheitstechnikStand }) {
  const k = stand.kennzahlen;
  return (
    <>
      {k.aufAnnahmen && <p className="hinweis hinweis-warnung">{ANNAHMEN_HINWEIS}</p>}
      <KpiReihe>
        <KpiCard titel="Brandabschnitte" wert={k.abschnitte}
                 vergleich={`${stand.matrix.abschnitteAusGruppen} aus Meldergruppen`} />
        <KpiCard titel="Meldergruppen" wert={k.gruppen} />
        <KpiCard titel="Ansteuerungen" wert={k.ansteuerungen} vergleich={`${k.ohneAbschnitt} ohne Abschnitt`} />
        <KpiCard titel="Zentralen" wert={k.zentralen} vergleich={`BMZ ${k.bmz} · RWA ${k.rwa}`} />
        <KpiCard titel="Warnungen" wert={k.warnungen} zustand={k.warnungen > 0 ? 'warnung' : 'gut'}
                 zustandText={k.warnungen > 0 ? 'prüfen' : 'keine'} />
        <KpiCard titel="Fehler" wert={k.fehler} zustand={k.fehler > 0 ? 'fehler' : 'gut'}
                 zustandText={k.fehler > 0 ? 'beheben' : 'keine'} />
      </KpiReihe>
      <Matrix stand={stand} />
      <Notstrom zeilen={stand.notstrom} />
      <Ringe zeilen={stand.ringe} />
      <Rwa linien={stand.rwaLinien} zentralen={stand.rwaZentralen} />
      <Vorgaben zeilen={stand.vorgaben} />
      {(stand.befunde.length > 0 || stand.matrix.befunde.length > 0) && (
        <ContentSection titel="Allgemeine Befunde">
          <Befundliste befunde={[...stand.befunde, ...stand.matrix.befunde]} />
        </ContentSection>
      )}
    </>
  );
}

function Matrix({ stand }: { stand: SicherheitstechnikStand }) {
  const [gewaehlt, setGewaehlt] = useState<StZeile | null>(null);
  const spalten: Spalte<StZeile>[] = [
    { schluessel: 'abschnitt', titel: 'Abschnitt', wert: (z) => z.abschnitt },
    { schluessel: 'gruppen', titel: 'auslösende Gruppen', art: 'zahl', wert: (z) => z.ausloesendeGruppen },
    { schluessel: 'melder', titel: 'Melder', art: 'zahl', wert: (z) => z.melder },
    ...stand.matrix.spalten.map((s): Spalte<StZeile> => ({
      schluessel: s.schluessel, titel: s.titel, einheit: s.wirkung, art: 'zahl',
      wert: (z) => zellenzahl(z, s.schluessel),
    })),
    {
      schluessel: 'befund', titel: 'Befund',
      wert: (z) => z.befunde.length,
      inhalt: (z) => (z.befunde.length === 0 ? '' : <StatusBadge zustand="warnung" text={`${z.befunde.length}`} klein />),
    },
  ];

  return (
    <ContentSection
      titel="Brandfallsteuermatrix"
      beschreibung="Planungsentwurf: Zeilen sind die Brandabschnitte (aus Quadrant und Geschoss abgeleitet — Annahme), Spalten die Ansteuerungen. Eine Zeile anklicken zeigt die Objekte.">
      <ul className="liste klein">
        {stand.matrix.spalten.map((s) => (
          <li key={s.schluessel}><strong>{s.titel}</strong>: {s.herkunft ?? '—'}
            {s.ohneAbschnitt > 0 ? ` · ${s.ohneAbschnitt} ohne Abschnitt` : ''}</li>
        ))}
      </ul>
      <DataTable name={`Abschnitte (${stand.matrix.zeilen.length})`} spalten={spalten} zeilen={stand.matrix.zeilen}
                 schluessel={(z) => z.abschnitt} beiZeileKlick={(z) => setGewaehlt(z)} dicht />
      {gewaehlt && (
        <div className="karte">
          <h3>{gewaehlt.abschnitt}</h3>
          <dl className="werte">
            <div><dt>auslösende Gruppen</dt><dd>{gewaehlt.gruppen.join(', ') || '—'}</dd></div>
            {gewaehlt.zellen.filter((c) => c.anzahl > 0).map((c) => (
              <div key={c.spalte}>
                <dt>{stand.matrix.spalten.find((s) => s.schluessel === c.spalte)?.titel ?? c.spalte}</dt>
                <dd>{c.objekte.join(', ')}</dd>
              </div>
            ))}
          </dl>
          <Befundliste befunde={gewaehlt.befunde} />
        </div>
      )}
    </ContentSection>
  );
}

function Notstrom({ zeilen }: { zeilen: StNotstrom[] }) {
  const spalten: Spalte<StNotstrom>[] = [
    { schluessel: 'zentrale', titel: 'Zentrale', wert: (z) => z.zentrale },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.zentralenart.toUpperCase() },
    { schluessel: 'ir', titel: 'I_R', einheit: 'A', art: 'zahl', nachkomma: 3, wert: (z) => z.ruhestromA },
    { schluessel: 'ia', titel: 'I_A', einheit: 'A', art: 'zahl', nachkomma: 3, wert: (z) => z.alarmstromA },
    { schluessel: 'tr', titel: 't_R', einheit: 'h', art: 'zahl', nachkomma: 1, wert: (z) => z.ruhezeitH },
    { schluessel: 'c', titel: 'C erforderlich', einheit: 'Ah', art: 'zahl', nachkomma: 1, wert: (z) => z.kapazitaetErforderlichAh },
    { schluessel: 'cb', titel: 'C verbaut', einheit: 'Ah', art: 'zahl', nachkomma: 1, wert: (z) => z.bestandAh },
    { schluessel: 'cmax', titel: 'max. Baureihe', einheit: 'Ah', art: 'zahl', nachkomma: 0, wert: (z) => z.hoechstAhBaureihe },
    { schluessel: 'nt', titel: 'Netzteil erf.', einheit: 'A', art: 'zahl', nachkomma: 2, wert: (z) => z.netzteilErforderlichA },
    { schluessel: 'ntv', titel: 'Netzteil', einheit: 'A', art: 'zahl', nachkomma: 2, wert: (z) => z.netzteilA },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} gegenAnnahmen={z.gegenAnnahmen} /> },
  ];
  return (
    <ContentSection titel="Notstrom der Zentralen"
                    beschreibung="C = (I_R · t_R + I_A · t_A) · f nach DIN EN 54-4 / DIN VDE 0833-1; Ladung 80 % in 24 h. * = gegen unbestätigte Vorgaben gerechnet.">
      <DataTable name={`Zentralen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.zentrale} suche={false} dicht />
      {zeilen.filter((z) => z.befunde.some((b) => !b.startsWith('HINWEIS'))).map((z) => (
        <div key={z.zentrale} className="karte">
          <h3>{z.zentrale}</h3>
          <p className="klein">{z.rechenweg.join(' · ')}</p>
          <Befundliste befunde={z.befunde} />
        </div>
      ))}
    </ContentSection>
  );
}

function Ringe({ zeilen }: { zeilen: StRing[] }) {
  const [nurBefund, setNurBefund] = useState(true);
  const gezeigt = nurBefund ? zeilen.filter((z) => z.status !== 'ok') : zeilen;
  const spalten: Spalte<StRing>[] = [
    { schluessel: 'linie', titel: 'Linie', wert: (z) => z.linie },
    { schluessel: 'zentrale', titel: 'Zentrale', wert: (z) => z.zentrale },
    { schluessel: 'laenge', titel: 'Länge', einheit: 'm', art: 'zahl', nachkomma: 0, wert: (z) => z.laengeM },
    { schluessel: 'leitung', titel: 'Leitung', wert: (z) => z.leitungsart },
    { schluessel: 'a', titel: 'A', einheit: 'mm²', art: 'zahl', nachkomma: 3, wert: (z) => z.querschnittMm2 },
    { schluessel: 'tn', titel: 'Teilnehmer', art: 'zahl', wert: (z) => z.teilnehmer },
    { schluessel: 'i', titel: 'I Alarm', einheit: 'A', art: 'zahl', nachkomma: 3, wert: (z) => z.stromA },
    { schluessel: 'lastfall', titel: 'Lastfall', wert: (z) => z.lastfall ?? null },
    { schluessel: 'du', titel: 'ΔU', einheit: 'V', art: 'zahl', nachkomma: 2, wert: (z) => z.spannungsfallV },
    { schluessel: 'ue', titel: 'U Ende', einheit: 'V', art: 'zahl', nachkomma: 2, wert: (z) => spannungEnde(z.spannungEndeV) },
    { schluessel: 'ueo', titel: 'U Ende obere Schranke', einheit: 'V', art: 'zahl', nachkomma: 2,
      wert: (z) => spannungEnde(z.spannungEndeObenV) },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} gegenAnnahmen={z.gegenAnnahmen} /> },
  ];
  return (
    <ContentSection titel="Ringnachweis BMA"
                    beschreibung="Maßgebend ist der Ringbruch an der Zentrale, gespeist von einer Seite: genau über die Teilstrecken (Melderadresse und Lage), sonst als Näherung mit verteilter Last; daneben die obere Schranke mit der ganzen Last am Ende. FEHLER nur, wenn der genaue Wert bzw. die Näherung unter der Mindestspannung liegt. R' je Ader aus der Vorgabe (0,8 mm: 36,6 Ω/km), sonst ρ / A.">
      <label className="klein">
        <input type="checkbox" checked={nurBefund} onChange={(e) => setNurBefund(e.target.checked)} />
        {' '}nur Linien mit Befund ({zeilen.filter((z) => z.status !== 'ok').length} von {zeilen.length})
      </label>
      <DataTable name={`Linien (${gezeigt.length})`} spalten={spalten} zeilen={gezeigt}
                 schluessel={(z) => z.linie} dicht />
    </ContentSection>
  );
}

function Rwa({ linien, zentralen }: { linien: StRwaLinie[]; zentralen: StRwaZentrale[] }) {
  const sl: Spalte<StRwaLinie>[] = [
    { schluessel: 'linie', titel: 'Linie', wert: (z) => z.linie },
    { schluessel: 'zentrale', titel: 'Zentrale', wert: (z) => z.zentrale },
    { schluessel: 'laenge', titel: 'Länge', einheit: 'm', art: 'zahl', nachkomma: 0, wert: (z) => z.laengeM },
    { schluessel: 'a', titel: 'A', einheit: 'mm²', art: 'zahl', nachkomma: 2, wert: (z) => z.querschnittMm2 },
    { schluessel: 'antriebe', titel: 'Antriebe', art: 'zahl', wert: (z) => z.antriebe },
    { schluessel: 'i', titel: 'I', einheit: 'A', art: 'zahl', nachkomma: 2, wert: (z) => z.stromA },
    { schluessel: 'du', titel: 'ΔU', einheit: '%', art: 'zahl', nachkomma: 1, wert: (z) => z.spannungsfallProzent },
    { schluessel: 'zul', titel: 'zulässig', einheit: '%', art: 'zahl', nachkomma: 0, wert: (z) => z.zulaessigProzent },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} gegenAnnahmen={z.gegenAnnahmen} /> },
  ];
  const sz: Spalte<StRwaZentrale>[] = [
    { schluessel: 'zentrale', titel: 'RWA-Zentrale', wert: (z) => z.zentrale },
    { schluessel: 'baureihe', titel: 'Baureihe', wert: (z) => z.baureihe },
    { schluessel: 'linien', titel: 'Linien', art: 'zahl', wert: (z) => z.linien },
    { schluessel: 'antriebe', titel: 'Antriebe', art: 'zahl', wert: (z) => z.antriebe },
    { schluessel: 'summe', titel: 'Σ I', einheit: 'A', art: 'zahl', nachkomma: 1, wert: (z) => z.summeAntriebsstromA },
    { schluessel: 'max', titel: 'max.', einheit: 'A', art: 'zahl', nachkomma: 0, wert: (z) => z.hoechstAntriebsstromA },
    { schluessel: 'befund', titel: 'Befund', wert: (z) => z.befunde.map((b) => zerlegeBefund(b).text).join(' · ') },
  ];
  return (
    <ContentSection titel="RWA-Antriebsleitungen und Zentralen"
                    beschreibung="ΔU bei 24 V DC mit allen Antrieben gleichzeitig; Antriebsstrom und zulässiger Spannungsfall sind Vorgaben, bis der Hersteller sie nennt.">
      <DataTable name={`RWA-Linien (${linien.length})`} spalten={sl} zeilen={linien}
                 schluessel={(z) => z.linie} suche={false} dicht />
      <DataTable name={`RWA-Zentralen (${zentralen.length})`} spalten={sz} zeilen={zentralen}
                 schluessel={(z) => z.zentrale} suche={false} dicht />
    </ContentSection>
  );
}

function Vorgaben({ zeilen }: { zeilen: StVorgabe[] }) {
  const spalten: Spalte<StVorgabe>[] = [
    { schluessel: 'feld', titel: 'Vorgabe', wert: (z) => z.feld },
    { schluessel: 'verwendung', titel: 'Verwendung', wert: (z) => z.verwendung },
    { schluessel: 'wert', titel: 'Wert', ausrichtung: 'rechts', wert: (z) => z.wert },
    { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.einheit },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (z) => (z.rueckfall ? `${z.herkunft} (Rückfall)` : z.herkunft) },
    { schluessel: 'bestaetigt', titel: 'bestätigt', art: 'status', wert: (z) => z.bestaetigt,
      inhalt: (z) => <StatusBadge zustand={z.bestaetigt ? 'gut' : 'warnung'} text={z.bestaetigt ? 'ja' : 'nein'} klein /> },
    { schluessel: 'norm', titel: 'Normbezug', wert: (z) => z.normbezug },
  ];
  return (
    <ContentSection titel="Vorgaben"
                    beschreibung="Gepflegt unter Elektro-Vorgaben (elektro_vorgaben). „Rückfall“ heißt: keine Zeile im Projekt, gerechnet mit dem Wert der Migration 1020.">
      <DataTable name={`Vorgaben (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.feld}|${z.verwendung ?? ''}`} dicht />
    </ContentSection>
  );
}
